import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { apiUrl } from '../config/api';
import { AuthContext } from '../context/AuthContext';
import { useEnvironment } from '../hooks/useEnvironment';
import { submitLoanWithContract } from '../utils/LoanAgreementUtils';
import { formatDateDMY } from '../utils/dateUtils';
import { toCurrency } from '../utils/adminMetrics';
import { sortRows, makeSortTh, TABLE_HEAD_ROW, TABLE_HEAD_TEXT, TABLE_ROW_LINE } from './UI/tableSort';
import { ShieldCheck, CheckCircle2, ChevronRight, AlertTriangle, Sparkles } from 'lucide-react';
import DocumentTile, { DOCUMENT_GRID } from './UI/DocumentTile';
import DisplayField, { FieldGroup } from './UI/DisplayField';
import { ReasonGroups } from './affordability/AffordabilityCheck';
import { reasonsForDisplay } from './affordability/affordabilityView';
import CreditReportField, { useCreditReportGuide } from './affordability/CreditReportField';
import { creditReportError } from './affordability/creditReportView';

// A term at the amount that can be approved straight away (within the
// recommended instalment), with its own monthly instalment and total; an
// older result without it: its maximum.
const atRecommended = (row) => (row && row.recommended_principal !== undefined && row.recommended_principal !== null
  ? { ...row, max_principal: row.recommended_principal, monthly_repayment: row.recommended_monthly_repayment ?? row.monthly_repayment, total_repayable: row.recommended_total_repayable ?? row.total_repayable }
  : row);

// The steps the client watches while their documents are checked. `to` is the
// server's progress_percent at which the step is done (see
// utils/prequalification/index.js in the API); reading the documents is by
// far the longest, so it is most of the bar.
const PREQ_STAGES = [
  {
    label: 'Read documents',
    to: 60,
    detail: 'We are reading every line of your payslip and bank statement. This is the longest step and usually takes about a minute.',
  },
  {
    label: 'Check they match',
    to: 75,
    detail: 'We are checking that the payslip and statement are yours, recent, and agree with each other.',
  },
  {
    label: 'Income & spending',
    to: 88,
    detail: 'We are working out your monthly income and regular spending from the statement.',
  },
  {
    label: 'Loan options',
    to: 100,
    detail: 'We are working out how much you can afford to repay and the loans you qualify for.',
  },
];

// How long the bar takes to close most of the gap to the end of the current
// step while the server is still on it, so it never sits still for a minute.
const CREEP_SECONDS = 40;

const formatElapsed = (seconds) => {
  const s = Math.max(0, Math.floor(seconds));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
};


export const BAND_STYLES = {
  A: 'bg-emerald-100 text-emerald-700 border-emerald-200 dark:bg-emerald-900/40 dark:text-emerald-300 dark:border-emerald-700/50',
  B: 'bg-blue-100 text-blue-700 border-blue-200 dark:bg-blue-900/40 dark:text-blue-300 dark:border-blue-700/50',
  C: 'bg-amber-100 text-amber-700 border-amber-200 dark:bg-amber-900/40 dark:text-amber-300 dark:border-amber-700/50',
  D: 'bg-rose-100 text-rose-700 border-rose-200 dark:bg-rose-900/40 dark:text-rose-300 dark:border-rose-700/50',
};

const PreQualification = ({
  clientId,
  onCompleted,
  onCancel,
  heading = 'Loan Pre-Qualification',
  allowReuse = true,
  // Coming back from the application form: open straight on this result,
  // with the repayment period that was picked still picked.
  initialAssessment = null,
  initialTermMonths = null,
  // Declined on affordability, the application was sent to the assessors for
  // review: called with the API's message and response.
  onReviewSent,
}) => {
  const { user } = React.useContext(AuthContext);
  // A client may not change what the document analysis filled in (staff may).
  const isClient = !user?.permissions?.includes('ManageLoans');
  const [phase, setPhase] = useState(initialAssessment ? 'review' : 'documents');
  const [preqPayslip, setPreqPayslip] = useState(null);
  const [preqStatements, setPreqStatements] = useState([]);
  // The client's credit report (ITC / TransUnion) and the date on it:
  // mandatory (Microlending Act s 24(4)), for clients and staff alike.
  const [preqBureau, setPreqBureau] = useState(null);
  const [preqBureauDate, setPreqBureauDate] = useState('');
  const creditGuide = useCreditReportGuide();
  const [preqError, setPreqError] = useState('');
  // How much the client needs, asked before the documents are checked.
  const [requestedAmount, setRequestedAmount] = useState('');
  const [isStartingVerification, setIsStartingVerification] = useState(false);
  // A click on a column of the terms table sorts it.
  const [termSort, setTermSort] = useState(null);
  const [assessmentId, setAssessmentId] = useState(initialAssessment?.id || null);
  const [assessment, setAssessment] = useState(initialAssessment);
  // The repayment period chosen from "What You Qualify For". The application
  // is filled in from it, so it must be chosen before going on.
  const [termMonths, setTermMonths] = useState(
    initialTermMonths != null ? Number(initialTermMonths) : null
  );
  const [termError, setTermError] = useState('');
  const [existingAssessment, setExistingAssessment] = useState(null);
  const [existingDismissed, setExistingDismissed] = useState(false);
  const [aiDisabled, setAiDisabled] = useState(false);
  // For the waiting screen: when the check started, when the server's
  // percentage last moved, and a clock ticking once a second meanwhile.
  const [verifyStartedAt, setVerifyStartedAt] = useState(null);
  const [pctChange, setPctChange] = useState({ pct: 0, at: 0 });
  const [nowTick, setNowTick] = useState(Date.now());
  // "Send for review" (declined on affordability): what the client would like.
  const [reviewForm, setReviewForm] = useState({ amount: '', months: '', deductionDay: '', reasonId: '' });
  const [reviewError, setReviewError] = useState('');
  const [reviewSending, setReviewSending] = useState(false);
  const [loanReasons, setLoanReasons] = useState([]);

  const env = useEnvironment();
  const isDemo = !!env?.demo;
  const aiEnabled = env?.aiEnabled !== false;
  const navigate = useNavigate();

  const handleClose = () => {
    if (onCancel) {
      onCancel();
      return;
    }
    navigate('/dashboard');
  };

  useEffect(() => {
    if (!clientId || !allowReuse || !aiEnabled) return;
    let cancelled = false;
    (async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await axios.get(apiUrl(`/api/clients/${clientId}/credit-score`), {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (cancelled) return;
        const data = res.data || null;
        if (data && data.status === 'Completed') {
          setExistingAssessment(data);
        }
      } catch {
        // No previous assessment (404) or unavailable — pre-qualification proceeds as normal
      }
    })();
    return () => { cancelled = true; };
  }, [clientId, allowReuse, aiEnabled]);

  useEffect(() => {
    if (phase !== 'verifying' || !assessmentId) return;
    let cancelled = false;
    let intervalId = null;
    const stopPolling = () => {
      if (intervalId) {
        clearInterval(intervalId);
        intervalId = null;
      }
    };
    const poll = async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await axios.get(apiUrl(`/api/prequalify/${assessmentId}`), {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (cancelled) return;
        const data = res.data || {};
        setAssessment(data);
        if (data.status && data.status !== 'Processing') {
          stopPolling();
          if (data.status === 'Completed') {
            setPhase('review');
          }
          // 'Blocked' and 'Failed' are rendered on the verifying screen
        }
      } catch (err) {
        if (cancelled) return;
        const statusCode = err.response?.status;
        if (err.response?.data?.code === 'AI_DISABLED') {
          stopPolling();
          setAiDisabled(true);
          return;
        }
        if (statusCode === 403 || statusCode === 404) {
          stopPolling();
          setAssessment({
            status: 'Failed',
            error_message: err.response?.data?.error || 'Assessment could not be found. Please try again.'
          });
        }
        // Transient errors (network/5xx): keep polling
      }
    };
    poll();
    intervalId = setInterval(poll, 2500);
    return () => {
      cancelled = true;
      stopPolling();
    };
  }, [phase, assessmentId]);

  const isProcessing = phase === 'verifying' && (!assessment?.status || assessment.status === 'Processing');

  // Start the clock when the check starts.
  useEffect(() => {
    if (phase === 'verifying' && assessmentId) {
      const now = Date.now();
      setVerifyStartedAt(now);
      setPctChange({ pct: 0, at: now });
    }
  }, [phase, assessmentId]);

  // Note each time the server's percentage moves, so the bar's creep starts
  // again from the new figure.
  const serverPct = Number(assessment?.progress_percent) || 0;
  useEffect(() => {
    setPctChange((prev) => (serverPct !== prev.pct ? { pct: serverPct, at: Date.now() } : prev));
  }, [serverPct]);

  useEffect(() => {
    if (!isProcessing) return undefined;
    const id = setInterval(() => setNowTick(Date.now()), 1000);
    return () => clearInterval(id);
  }, [isProcessing]);

  // Leaving now means starting again, so the browser asks first.
  useEffect(() => {
    if (!isProcessing) return undefined;
    const warn = (e) => {
      e.preventDefault();
      e.returnValue = '';
      return '';
    };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [isProcessing]);

  const handleStartVerification = async () => {
    if (isDemo || isStartingVerification) return;
    setPreqError('');
    const requested = parseFloat(String(requestedAmount).replace(/[\s,]/g, ''));
    if (!Number.isFinite(requested) || requested <= 0) {
      setPreqError('Please enter how much you need.');
      return;
    }
    if (!preqPayslip) {
      setPreqError('Please upload your latest payslip.');
      return;
    }
    const statement = preqStatements.filter(Boolean)[0];
    if (!statement) {
      setPreqError('Please upload your bank statement covering the last 3 months.');
      return;
    }
    const bureauError = creditReportError({
      file: preqBureau,
      reportDate: preqBureauDate,
      required: true,
      messages: creditGuide?.messages,
      maxAgeDays: creditGuide?.max_age_days ?? 30,
    });
    if (bureauError) {
      setPreqError(bureauError);
      return;
    }
    // The file type and size are checked by the API; a refused file comes
    // back with its message, shown below as it is.
    setIsStartingVerification(true);
    try {
      const token = localStorage.getItem('token');
      const fd = new FormData();
      if (clientId) fd.append('client_id', String(clientId));
      fd.append('payslip', preqPayslip);
      fd.append('statement', statement);
      fd.append('requested_amount', String(requested));
      if (preqBureau) {
        fd.append('bureau_report', preqBureau);
        fd.append('bureau_report_date', preqBureauDate);
      }
      const res = await axios.post(apiUrl('/api/prequalify'), fd, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
      });
      const newId = res.data?.assessment_id;
      if (!newId) {
        throw new Error('Verification could not be started. Please try again.');
      }
      setAssessment(null);
      setAssessmentId(newId);
      setPhase('verifying');
    } catch (err) {
      if (err.response?.data?.code === 'AI_DISABLED') {
        setAiDisabled(true);
      } else {
        setPreqError(err.response?.data?.error || err.message || 'Failed to start verification. Please try again.');
      }
    }
    setIsStartingVerification(false);
  };

  // Declined on affordability: the application may still go to the assessors
  // (the API's `eligibility.can_request_review`).
  const canRequestReview = phase === 'review'
    && assessment?.eligibility?.can_proceed === false
    && assessment?.eligibility?.can_request_review === true;

  // The review form starts on the assessment's deduction day and its first
  // term; the loan reasons are the application form's.
  useEffect(() => {
    if (!canRequestReview) return undefined;
    setReviewForm((prev) => ({
      ...prev,
      deductionDay: prev.deductionDay || String(assessment?.recommended_deduction_day || ''),
      amount: prev.amount || String(assessment?.eligibility?.prefilled?.requested_amount || assessment?.requested_amount || ''),
    }));
    let cancelled = false;
    (async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await axios.get(apiUrl('/api/loan-reasons'), { headers: { Authorization: `Bearer ${token}` } });
        if (!cancelled) setLoanReasons(Array.isArray(res.data) ? res.data : []);
      } catch {
        if (!cancelled) setLoanReasons([]);
      }
    })();
    return () => { cancelled = true; };
  }, [canRequestReview, assessment?.recommended_deduction_day]);

  const handleSendForReview = async (e) => {
    e?.preventDefault?.();
    if (!assessment?.id || reviewSending) return;
    setReviewError('');
    const amount = parseFloat(reviewForm.amount);
    if (!Number.isFinite(amount) || amount <= 0) {
      setReviewError('Enter the amount you would like.');
      return;
    }
    if (!reviewForm.months) {
      setReviewError('Choose how many months you would like to repay over.');
      return;
    }
    setReviewSending(true);
    const result = await submitLoanWithContract(
      {
        principal_amount: String(amount),
        duration_months: String(reviewForm.months),
        deduction_day: reviewForm.deductionDay,
        loan_reason_id: reviewForm.reasonId || null,
      },
      clientId,
      undefined,
      undefined,
      undefined,
      undefined,
      assessment.id,
      { riskReview: true }
    );
    setReviewSending(false);
    if (!result.success) {
      setReviewError(result.message);
      return;
    }
    const message = result.data?.message || 'Your application was sent to our assessors for review.';
    if (onReviewSent) {
      onReviewSent(message, result.data);
      return;
    }
    navigate('/dashboard');
  };

  const handleBackToDocuments = () => {
    // Keep chosen files so the problem document alone can be replaced
    setAssessment(null);
    setAssessmentId(null);
    setTermMonths(null);
    setTermError('');
    setPreqError('');
    setPhase('documents');
  };

  // Whether the client may go on and on which terms is the API's call (the
  // assessment's `eligibility`).
  const availableTerms = (assessment?.eligibility?.available_terms || []).map(Number);
  const chosenTerm = (assessment?.eligibility?.can_proceed
    ? (assessment?.qualification_matrix || []).map(atRecommended).find((row) => Number(row.months) === Number(termMonths) && availableTerms.includes(Number(row.months)))
    : null) || null;

  const handleContinueToApplication = () => {
    if (!assessment) return;
    if (!chosenTerm) {
      setTermError('Please choose a repayment period above before continuing.');
      return;
    }
    onCompleted?.(
      assessment,
      { payslipFile: preqPayslip, statementFiles: preqStatements.filter(Boolean) },
      { termMonths: Number(chosenTerm.months), term: chosenTerm }
    );
  };

  // A recent result can be used again, but it still goes through the result
  // screen so a repayment period is chosen from it.
  const handleUseExisting = () => {
    if (!existingAssessment) return;
    setAssessment(existingAssessment);
    setAssessmentId(existingAssessment.id || null);
    setTermMonths(null);
    setTermError('');
    setPhase('review');
  };

  const renderExistingCard = () => {
    if (!existingAssessment || existingDismissed) return null;
    const assessedOn = existingAssessment.created_at
      ? formatDateDMY(existingAssessment.created_at)
      : 'an earlier date';
    return (
      <div className="mt-4 rounded-lg border border-emerald-200 dark:border-emerald-800/40 bg-emerald-50 dark:bg-emerald-900/20 p-3">
        <div className="flex items-start gap-2">
          <CheckCircle2 size={14} className="mt-0.5 text-emerald-600 dark:text-emerald-400 flex-shrink-0" />
          <div className="min-w-0 flex-1">
            <p className="text-xs text-emerald-700 dark:text-emerald-300">
              This client was assessed on <strong>{assessedOn}</strong>
            </p>
            <div className="mt-2 flex flex-wrap gap-2">
              <button
                type="button"
                onClick={handleUseExisting}
                className="px-2.5 py-1 text-xs font-semibold rounded-md transition-colors bg-emerald-600 text-white hover:bg-emerald-700"
              >
                Use this assessment
              </button>
              <button
                type="button"
                onClick={() => setExistingDismissed(true)}
                className="px-2.5 py-1 text-xs font-medium rounded-md transition-colors bg-white text-emerald-700 border border-emerald-200 hover:bg-emerald-100 dark:bg-slate-800 dark:text-emerald-300 dark:border-emerald-800/40 dark:hover:bg-slate-700"
              >
                Run a new assessment
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderDocumentsPhase = () => (
    <div className="rounded-xl border border-blue-100 dark:border-slate-700 bg-white dark:bg-slate-800 shadow-sm overflow-hidden">
      <div className="h-0.5 bg-gradient-to-r from-blue-500 to-indigo-500" />
      <div className="p-4 sm:p-6">
        <div className="flex items-center gap-2.5">
          <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-blue-500 to-indigo-600 text-white flex items-center justify-center flex-shrink-0">
            <ShieldCheck size={18} />
          </div>
          <div>
            <h3 className="text-sm font-semibold text-gray-900 dark:text-slate-100">{heading}</h3>
            <p className="text-[11px] text-gray-500 dark:text-slate-400">Verify your documents to see how much you qualify for before applying</p>
          </div>
        </div>

        <p className="mt-4 text-xs text-gray-600 dark:text-slate-300">
          Before you can apply for a loan, we need your <strong>latest payslip</strong>, <strong>one bank statement document covering the last 3 months</strong> and your <strong>credit report</strong>.
          We use them to verify your income and calculate the loan amount you qualify for. This takes about a minute.
        </p>

        {isDemo && (
          <p className="mt-3 text-xs text-amber-700 dark:text-amber-400">
            <strong>Pre-qualification is turned off in the demo environment.</strong> Document verification sends uploads to an external AI service and draws on a shared API quota, so it is disabled in this public sandbox. It is fully available in a real installation.
          </p>
        )}

        {renderExistingCard()}

        <div className="mt-4">
          <label htmlFor="preq-requested-amount" className="block text-sm font-medium text-gray-800 dark:text-slate-200 mb-2">
            How much do you need? (N$)
          </label>
          <input
            id="preq-requested-amount"
            type="number"
            inputMode="decimal"
            min="1"
            step="0.01"
            value={requestedAmount}
            onChange={(e) => { setRequestedAmount(e.target.value); setPreqError(''); }}
            placeholder="e.g. 5000"
            className="w-full sm:w-64 focus:ring-blue-400 focus:border-transparent"
          />
          <p className="mt-1 text-[11px] text-gray-500 dark:text-slate-400">We check whether you can afford it, and tell you if you qualify for less.</p>
        </div>

        <div className="mt-4">
          <p className="text-sm font-medium text-gray-800 dark:text-slate-200 mb-2">Required Documents</p>
          <div className={DOCUMENT_GRID}>
            <DocumentTile
              type="Payslip"
              hint="Latest month"
              file={preqPayslip}
              onPick={(f) => { setPreqPayslip(f); setPreqError(''); }}
              onRemove={() => setPreqPayslip(null)}
              required
            />
            <DocumentTile
              type="Bank Statement"
              hint="One document covering the last 3 months"
              file={preqStatements[0] || null}
              onPick={(f) => { setPreqStatements([f]); setPreqError(''); }}
              onRemove={() => setPreqStatements([])}
              required
            />
            <CreditReportField
              guide={creditGuide}
              file={preqBureau}
              onPick={(f) => { setPreqBureau(f); setPreqError(''); }}
              onRemove={() => setPreqBureau(null)}
              reportDate={preqBureauDate}
              onDate={(d) => { setPreqBureauDate(d); setPreqError(''); }}
              required
            />
          </div>
        </div>

        {preqError && (
          <div className="mt-3 rounded-lg border border-rose-200 dark:border-rose-800/40 bg-rose-50 dark:bg-rose-900/20 px-3 py-2 text-xs text-rose-700 dark:text-rose-300">
            {preqError}
          </div>
        )}

        <div className="mt-5 pt-4 border-t border-gray-100 dark:border-slate-700 flex justify-end gap-2">
          {onCancel && (
            <button
              type="button"
              onClick={onCancel}
              className="px-4 py-2 rounded-lg text-xs font-medium bg-gray-100 dark:bg-slate-700 text-gray-700 dark:text-slate-200 hover:bg-gray-200 dark:hover:bg-slate-600 transition"
            >
              Cancel
            </button>
          )}
          <button
            type="button"
            onClick={handleStartVerification}
            disabled={isDemo || isStartingVerification}
            title={isDemo ? 'Pre-qualification is disabled in the demo environment.' : undefined}
            className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-xs font-semibold transition ${
              isDemo
                ? 'bg-gray-200 text-gray-400 cursor-not-allowed dark:bg-slate-700 dark:text-slate-500'
                : isStartingVerification
                ? 'bg-blue-200 text-blue-700 cursor-wait dark:bg-blue-900/50 dark:text-blue-300'
                : 'bg-blue-600 text-white hover:bg-blue-700 shadow-sm'
            }`}
          >
            {isStartingVerification ? (
              <span className="animate-spin rounded-full h-3.5 w-3.5 border-2 border-blue-400 border-t-transparent" />
            ) : (
              <Sparkles size={14} />
            )}
            {isDemo ? 'Disabled in demo' : isStartingVerification ? 'Verifying…' : 'Start Verification'}
          </button>
        </div>
      </div>
    </div>
  );

  const renderVerifyingPhase = () => {
    const status = assessment?.status || 'Processing';
    const reportedPct = Math.max(0, Math.min(100, serverPct));
    const stepIndex = PREQ_STAGES.findIndex((s) => reportedPct < s.to);
    const currentStep = status === 'Completed' ? PREQ_STAGES.length : (stepIndex === -1 ? PREQ_STAGES.length - 1 : stepIndex);
    // Between the server's updates the bar keeps moving towards the end of
    // the step it is on, slowing as it nears it, and never reaching it until
    // the server says the step is done.
    const stepEnd = PREQ_STAGES[Math.min(currentStep, PREQ_STAGES.length - 1)].to - 2;
    const sinceChange = Math.max(0, (nowTick - pctChange.at) / 1000);
    const creep = stepEnd > reportedPct
      ? (stepEnd - reportedPct) * (1 - Math.exp(-sinceChange / CREEP_SECONDS))
      : 0;
    const pct = Math.round(Math.min(99, reportedPct + creep));
    const elapsed = verifyStartedAt ? (nowTick - verifyStartedAt) / 1000 : 0;
    const issues = assessment?.validation?.issues || [];

    return (
      <div className="rounded-xl border border-blue-100 dark:border-slate-700 bg-white dark:bg-slate-800 shadow-sm overflow-hidden">
        <div className="h-0.5 bg-gradient-to-r from-blue-500 to-indigo-500" />
        <div className="p-4 sm:p-6">
          <div className="flex items-center gap-2.5">
            <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-blue-500 to-indigo-600 text-white flex items-center justify-center flex-shrink-0">
              {status === 'Processing' ? (
                <span className="animate-spin rounded-full h-4 w-4 border-2 border-white/40 border-t-white" />
              ) : (
                <ShieldCheck size={18} />
              )}
            </div>
            <div>
              <h3 className="text-sm font-semibold text-gray-900 dark:text-slate-100">Verifying Your Documents</h3>
              <p className="text-[11px] text-gray-500 dark:text-slate-400">We are checking your payslip and bank statement — this usually takes 1 to 2 minutes</p>
            </div>
          </div>

          {status === 'Blocked' ? (
            <div className="mt-4">
              <div className="rounded-lg border border-rose-200 dark:border-rose-800/40 bg-rose-50 dark:bg-rose-900/20 p-3">
                <div className="flex items-center gap-2 mb-2">
                  <AlertTriangle size={14} className="text-rose-600 dark:text-rose-400 flex-shrink-0" />
                  <p className="text-xs font-semibold text-rose-700 dark:text-rose-300">We could not verify your documents</p>
                </div>
                <ul className="space-y-1.5">
                  {issues.map((issue, idx) => (
                    <li
                      key={`${issue.code || 'issue'}-${idx}`}
                      className={`text-xs rounded-md border-l-4 px-2.5 py-1.5 ${
                        issue.severity === 'critical'
                          ? 'border-rose-400 bg-rose-100/60 text-rose-700 dark:bg-rose-900/30 dark:text-rose-300'
                          : 'border-amber-400 bg-amber-50 text-amber-700 dark:bg-amber-900/20 dark:text-amber-300'
                      }`}
                    >
                      {issue.message}
                    </li>
                  ))}
                  {issues.length === 0 && (
                    <li className="text-xs text-rose-700 dark:text-rose-300">Your documents did not pass validation. Please replace them and try again.</li>
                  )}
                </ul>
              </div>
              <div className="mt-4 flex justify-end">
                <button
                  type="button"
                  onClick={handleBackToDocuments}
                  className="px-2.5 py-1 text-xs font-medium rounded-md transition-colors bg-blue-50 text-blue-600 hover:bg-blue-100 dark:bg-blue-900/30 dark:text-blue-300"
                >
                  Fix documents &amp; try again
                </button>
              </div>
            </div>
          ) : status === 'Failed' ? (
            <div className="mt-4">
              <div className="rounded-lg border border-rose-200 dark:border-rose-800/40 bg-rose-50 dark:bg-rose-900/20 p-3">
                <div className="flex items-center gap-2">
                  <AlertTriangle size={14} className="text-rose-600 dark:text-rose-400 flex-shrink-0" />
                  <p className="text-xs font-semibold text-rose-700 dark:text-rose-300">Verification failed</p>
                </div>
                <p className="mt-1.5 text-xs text-rose-700 dark:text-rose-300">
                  {assessment?.error_message || 'Something went wrong while processing your documents. Please try again.'}
                </p>
              </div>
              <div className="mt-4 flex justify-end">
                <button
                  type="button"
                  onClick={handleBackToDocuments}
                  className="px-2.5 py-1 text-xs font-medium rounded-md transition-colors bg-blue-50 text-blue-600 hover:bg-blue-100 dark:bg-blue-900/30 dark:text-blue-300"
                >
                  Try again
                </button>
              </div>
            </div>
          ) : (
            <div className="mt-5">
              <div className="rounded-lg border border-amber-200 dark:border-amber-800/40 bg-amber-50 dark:bg-amber-900/20 p-3 mb-4 flex gap-2">
                <AlertTriangle size={15} className="text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-xs font-semibold text-amber-800 dark:text-amber-300">Please keep this page open until it finishes</p>
                  <p className="mt-0.5 text-xs text-amber-700 dark:text-amber-300/90">
                    Reading your documents usually takes 1 to 2 minutes. If you leave or refresh this page now,
                    you may have to upload your documents and start again.
                  </p>
                </div>
              </div>

              <div className="flex items-center justify-between mb-1.5 gap-3">
                <span className="text-xs font-semibold text-slate-700 dark:text-slate-200">
                  {assessment?.progress_stage && assessment.progress_stage !== 'Queued'
                    ? assessment.progress_stage
                    : 'Starting the check…'}
                </span>
                <span className="text-xs font-semibold text-emerald-600 dark:text-emerald-400 tabular-nums">{pct}%</span>
              </div>
              <div
                className="w-full h-2.5 rounded-full bg-gray-200 dark:bg-slate-700 overflow-hidden"
                role="progressbar"
                aria-valuenow={pct}
                aria-valuemin={0}
                aria-valuemax={100}
                aria-label="Document check progress"
              >
                <div
                  className="h-full rounded-full bg-emerald-500 transition-[width] duration-1000 ease-linear relative overflow-hidden"
                  style={{ width: `${Math.max(pct, 3)}%` }}
                >
                  <span className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent animate-pulse" />
                </div>
              </div>
              <div className="flex items-center justify-between mt-1.5 text-[11px] text-gray-500 dark:text-slate-400">
                <span>{PREQ_STAGES[Math.min(currentStep, PREQ_STAGES.length - 1)].detail}</span>
                <span className="flex-shrink-0 ml-3 tabular-nums">
                  {formatElapsed(elapsed)} elapsed
                </span>
              </div>
              {elapsed > 150 && (
                <p className="mt-2 text-[11px] text-gray-500 dark:text-slate-400">
                  This is taking longer than usual, which can happen with a long bank statement. It is still working — please keep waiting.
                </p>
              )}

              <div className="flex items-center gap-1.5 sm:gap-2 mt-4 flex-wrap">
                {PREQ_STAGES.map(({ label: stage }, i) => (
                  <React.Fragment key={stage}>
                    {i > 0 && <ChevronRight size={16} className="flex-shrink-0 text-gray-300 dark:text-slate-600" />}
                    <div
                      className={`flex-1 min-w-[7rem] text-center px-2 sm:px-3 py-2.5 rounded-lg text-xs font-semibold transition-colors ${
                        currentStep === i
                          ? 'bg-blue-600 text-white shadow-sm'
                          : currentStep > i
                          ? 'bg-emerald-50 text-emerald-700 border border-emerald-200 dark:bg-emerald-900/20 dark:text-emerald-300 dark:border-emerald-800/40'
                          : 'bg-gray-100 text-gray-500 border border-gray-200 dark:bg-slate-700 dark:text-slate-300 dark:border-slate-600'
                      }`}
                    >
                      {currentStep > i && <CheckCircle2 size={13} className="inline mr-1 -mt-0.5" />}
                      {stage}
                    </div>
                  </React.Fragment>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderReviewPhase = () => {
    // Whether the client may go on (a test account always may; its failed
    // checks are still listed in the reasons) and the message when not: the
    // API's `eligibility`.
    const verdict = assessment?.eligibility || null;
    const blocked = !verdict?.can_proceed;
    const afford = assessment?.affordability || {};
    const matrix = (assessment?.qualification_matrix || []).map(atRecommended);
    // Asked for more than any term allows: what they do qualify for.
    const askedFor = Number(verdict?.prefilled?.requested_amount) || 0;
    const bestRow = matrix.filter((r) => Number(r.max_principal) > 0).sort((a, b) => Number(b.max_principal) - Number(a.max_principal))[0] || null;
    const askedTooMuch = askedFor > 0 && bestRow && askedFor > Number(bestRow.max_principal);
    const termTh = makeSortTh(termSort, setTermSort);
    const termHead = { className: `px-3 py-2 ${TABLE_HEAD_TEXT}` };
    const reasons = assessment?.decision_reasons || [];
    const shownReasons = reasonsForDisplay(afford.decision_report?.affordability?.reasons, reasons);
    const analysis = assessment?.analysis || {};
    const affordFields = [
      { label: 'Avg Monthly Income', value: analysis.avg_monthly_income },
      { label: 'Monthly Expenses', value: analysis.monthly_expenses },
      { label: 'Existing Commitments', value: afford.existing_commitments },
      { label: 'Disposable Income', value: afford.disposable_income },
      { label: 'Max Installment', value: afford.max_installment },
      { label: 'Recommended Amount', value: afford.recommended_amount },
    ];

    return (
      <div className="space-y-4">
        <div className="rounded-xl border border-blue-100 dark:border-slate-700 bg-white dark:bg-slate-800 shadow-sm overflow-hidden">
          <div className="h-0.5 bg-gradient-to-r from-blue-500 to-indigo-500" />
          <div className="p-4 sm:p-6">
            <div className="flex items-center gap-2.5">
              <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-blue-500 to-indigo-600 text-white flex items-center justify-center flex-shrink-0">
                <ShieldCheck size={18} />
              </div>
              <div>
                <h3 className="text-sm font-semibold text-gray-900 dark:text-slate-100">Your Pre-Qualification Result</h3>
                <p className="text-[11px] text-gray-500 dark:text-slate-400">Based on your payslip and bank statement</p>
              </div>
            </div>

            {/* The legal test's result (Microlending Act / NAMFISA ML.S.4):
                no credit score or band. */}
            <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
              {/* "Pre-qualified up to N$X, subject to credit bureau check":
                  the bureau report comes on the application. */}
              <DisplayField label="Result">{verdict?.result_label || assessment?.recommendation || null}</DisplayField>
              <DisplayField label="You can repay a month" tone="strong">{afford.max_installment != null ? toCurrency(afford.max_installment) : null}</DisplayField>
            </div>

            {askedTooMuch && (
              <div className="mt-4 rounded-lg border border-emerald-200 dark:border-emerald-800/40 bg-emerald-50 dark:bg-emerald-900/20 p-3 flex items-start gap-2">
                <CheckCircle2 size={15} className="mt-0.5 flex-shrink-0 text-emerald-600 dark:text-emerald-400" />
                <p className="text-xs text-emerald-900 dark:text-emerald-200">
                  You asked for <strong>{toCurrency(askedFor)}</strong>. You qualify for up to <strong>{toCurrency(bestRow.max_principal)}</strong> over {bestRow.months} month{Number(bestRow.months) === 1 ? '' : 's'}, repaid at {toCurrency(bestRow.monthly_repayment)} a month.
                  {!blocked && ' Choose a period below: your application starts at the most you qualify for over it.'}
                </p>
              </div>
            )}

            {/* The same reasons as on the staff screen, grouped by how serious
                they are, each with its side red (declined), yellow (to
                review) or green (a note). */}
            {shownReasons.length > 0 && (
              <div className="mt-4">
                <p className="text-sm font-medium text-gray-800 dark:text-slate-200">Decision Reasons</p>
                <ReasonGroups reasons={shownReasons} look="edge" />
              </div>
            )}

            <div className="mt-4 grid grid-cols-2 md:grid-cols-4 xl:grid-cols-6 gap-4">
              <FieldGroup title="Affordability Summary" tone="blue" wide>
                {affordFields.map(({ label, value }) => (
                  <DisplayField key={label} label={label}>{value != null ? toCurrency(value) : null}</DisplayField>
                ))}
                {assessment?.recommended_deduction_day != null && (
                  <DisplayField label="Deduction Day" className="col-span-2">
                    {Number(assessment.recommended_deduction_day) === 31 ? 'The last day of each month' : `Day ${assessment.recommended_deduction_day} of each month`} (the day your salary comes in)
                  </DisplayField>
                )}
              </FieldGroup>
            </div>

            {matrix.length > 0 && (
              <div className="mt-4">
                <p className="text-sm font-medium text-gray-800 dark:text-slate-200 mb-1">What You Qualify For</p>
                {!blocked && (
                  <p className="text-xs text-gray-600 dark:text-slate-300 mb-2">
                    Choose how many months you want to repay over. Your application will be filled in with that period and its maximum amount.
                  </p>
                )}
                <div
                  className={`overflow-x-auto ${termError ? 'border-y-2 border-rose-300 dark:border-rose-700' : ''}`}
                  role={!blocked ? 'radiogroup' : undefined}
                  aria-label="Repayment period"
                >
                  <table className="min-w-full text-xs">
                    <thead>
                      <tr className={TABLE_HEAD_ROW}>
                        {!blocked && <th className="px-3 py-2 w-10"><span className="sr-only">Choose</span></th>}
                        {termTh('Term', 'term', (r) => Number(r.months), termHead)}
                        {termTh('Max Amount', 'max', (r) => Number(r.max_principal), termHead)}
                        {termTh('Monthly', 'monthly', (r) => Number(r.monthly_repayment), termHead)}
                        {termTh('Total', 'total', (r) => Number(r.total_repayable), termHead)}
                      </tr>
                    </thead>
                    <tbody>
                      {sortRows(matrix, termSort).map((row, idx) => {
                        const months = Number(row.months);
                        const available = !blocked && availableTerms.includes(months);
                        const selected = available && months === Number(termMonths);
                        const choose = () => {
                          if (!available) return;
                          setTermMonths(months);
                          setTermError('');
                        };
                        return (
                          <tr
                            key={row.months}
                            onClick={choose}
                            className={`${TABLE_ROW_LINE} transition-colors ${
                              selected
                                ? 'bg-blue-600/10 dark:bg-blue-500/20 outline outline-2 -outline-offset-2 outline-blue-500'
                                : idx % 2 === 0 ? 'bg-white dark:bg-slate-800' : 'bg-gray-50/60 dark:bg-slate-900/20'
                            } ${available ? 'cursor-pointer hover:bg-blue-50 dark:hover:bg-slate-700/60' : !blocked ? 'opacity-50' : ''}`}
                          >
                            {!blocked && (
                              <td className="px-3 py-2">
                                <input
                                  type="radio"
                                  name="preq-term"
                                  value={months}
                                  checked={selected}
                                  disabled={!available}
                                  onChange={choose}
                                  aria-label={`${months} month${months === 1 ? '' : 's'}, up to ${toCurrency(row.max_principal)}`}
                                  className="h-4 w-4 accent-blue-600 cursor-pointer disabled:cursor-not-allowed"
                                />
                              </td>
                            )}
                            <td className="px-3 py-2 font-semibold text-gray-900 dark:text-slate-100">
                              {months} month{months === 1 ? '' : 's'}
                              {!blocked && !available && (
                                <span className="ml-1 font-normal text-[11px] text-gray-500 dark:text-slate-400">(not available)</span>
                              )}
                            </td>
                            <td className="px-3 py-2 text-gray-700 dark:text-slate-300">{toCurrency(row.max_principal)}</td>
                            <td className="px-3 py-2 text-gray-700 dark:text-slate-300">{toCurrency(row.monthly_repayment)}</td>
                            <td className="px-3 py-2 text-gray-700 dark:text-slate-300">{toCurrency(row.total_repayable)}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                {termError && <p className="mt-1.5 text-xs text-rose-600 dark:text-rose-400">{termError}</p>}
                {!blocked && (
                  <div className="mt-3 rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900/30 p-3">
                    <p className="text-[11px] font-semibold text-slate-600 dark:text-slate-300 uppercase tracking-wide">How these amounts are worked out</p>
                    <ul className="mt-1.5 space-y-1 text-xs text-gray-600 dark:text-slate-300 list-disc pl-4">
                      {afford.max_installment != null && (
                        <li>
                          You can afford repayments of up to <strong>{toCurrency(afford.max_installment)} a month</strong> — part of
                          what is left of your income after your existing debit orders, loans and living costs.
                        </li>
                      )}
                      <li>
                        Each amount is the most you can borrow over that many months while keeping the monthly repayment
                        within that limit. A longer period lets you borrow more, but you repay for longer.
                      </li>
                      {afford.interest_rate_pct != null ? (
                        <li>
                          Interest is a fixed <strong>{Number(afford.interest_rate_pct)}%</strong> of the amount you borrow, charged
                          once — not every month.
                        </li>
                      ) : (
                        <li>The amounts include the interest on the loan.</li>
                      )}
                      <li>
                        The amounts and monthly repayments here are the loan and its interest only. Fees such as the NAMFISA
                        levy{afford.levy_pct != null ? <> ({Number(afford.levy_pct)}% of the amount)</> : null} and
                        {afford.duty_stamp != null ? <> {toCurrency(afford.duty_stamp)}</> : null} stamp duty
                        are added on your application, so the monthly payment there is a little higher.
                      </li>
                      <li>Everything you repay is split evenly across the months, and taken on your deduction day each month.</li>
                    </ul>
                  </div>
                )}
                {chosenTerm && (
                  <p className="mt-2 text-xs text-emerald-700 dark:text-emerald-300 flex items-center gap-1.5">
                    <CheckCircle2 size={13} className="flex-shrink-0" />
                    You chose {chosenTerm.months} month{Number(chosenTerm.months) === 1 ? '' : 's'}: up to {toCurrency(chosenTerm.max_principal)}, repaid at {toCurrency(chosenTerm.monthly_repayment)} a month.
                  </p>
                )}
              </div>
            )}

            {blocked ? (
              <div className="mt-4">
                <div className="rounded-lg border border-rose-200 dark:border-rose-800/40 bg-rose-50 dark:bg-rose-900/20 p-3">
                  <p className="text-xs font-semibold text-rose-700 dark:text-rose-300">You do not currently qualify for a loan</p>
                  <p className="mt-1 text-xs text-rose-700 dark:text-rose-300">
                    {verdict?.message || 'Based on your current income, expenses and commitments, we cannot offer you a loan right now.'} Improving your disposable income or reducing existing commitments may change this — you are welcome to try again later.
                  </p>
                </div>
                {canRequestReview && (
                  <form onSubmit={handleSendForReview} className="mt-3 rounded-lg border border-amber-200 dark:border-amber-800/40 bg-amber-50 dark:bg-amber-900/20 p-3">
                    <div className="flex items-start gap-2">
                      <AlertTriangle size={14} className="mt-0.5 flex-shrink-0 text-amber-600 dark:text-amber-400" />
                      <p className="text-xs text-amber-800 dark:text-amber-200">{verdict?.review_message}</p>
                    </div>
                    <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                      <div>
                        <label className="block text-sm font-medium text-gray-800 dark:text-slate-200 mb-1">Amount you would like (N$)</label>
                        <input
                          type="number"
                          min="1"
                          step="0.01"
                          required
                          value={reviewForm.amount}
                          onChange={(e) => setReviewForm((f) => ({ ...f, amount: e.target.value }))}
                          placeholder="N$"
                          className="w-full focus:ring-amber-400"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-800 dark:text-slate-200 mb-1">Repay over</label>
                        <select
                          required
                          value={reviewForm.months}
                          onChange={(e) => setReviewForm((f) => ({ ...f, months: e.target.value }))}
                          className="w-full focus:ring-amber-400"
                        >
                          <option value="">Select term</option>
                          {(matrix.length ? matrix.map((row) => Number(row.months)) : [1, 2, 3, 4, 5, 6]).map((m) => (
                            <option key={m} value={m}>{m} month{m === 1 ? '' : 's'}</option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-800 dark:text-slate-200 mb-1">Deduction Day (1-31)</label>
                        <input
                          type="number"
                          min={1}
                          max={31}
                          required
                          value={reviewForm.deductionDay}
                          readOnly={isClient && !!assessment?.eligibility?.prefilled?.deduction_day}
                          title={isClient && assessment?.eligibility?.prefilled?.deduction_day ? 'From your salary date' : undefined}
                          onChange={(e) => setReviewForm((f) => ({ ...f, deductionDay: e.target.value }))}
                          className="w-full focus:ring-amber-400"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-800 dark:text-slate-200 mb-1">Loan Reason</label>
                        <select
                          required={loanReasons.length > 0}
                          value={reviewForm.reasonId}
                          onChange={(e) => setReviewForm((f) => ({ ...f, reasonId: e.target.value }))}
                          className="w-full focus:ring-amber-400"
                        >
                          <option value="">Select reason</option>
                          {loanReasons.map((r) => (
                            <option key={r.id} value={r.id}>{r.name}</option>
                          ))}
                        </select>
                      </div>
                    </div>
                    {reviewError && <p className="mt-2 text-xs text-rose-600 dark:text-rose-400">{reviewError}</p>}
                    <div className="mt-3 flex flex-wrap justify-end gap-2">
                      <button
                        type="button"
                        onClick={handleClose}
                        className="px-3 py-1.5 text-xs font-medium rounded-md transition-colors bg-gray-100 text-gray-600 hover:bg-gray-200 dark:bg-slate-700 dark:text-slate-300"
                      >
                        Close
                      </button>
                      <button
                        type="submit"
                        disabled={reviewSending || isDemo}
                        className="px-4 py-1.5 rounded-md text-xs font-semibold bg-amber-600 text-white hover:bg-amber-700 shadow-sm transition disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {reviewSending ? 'Sending…' : 'Send for review'}
                      </button>
                    </div>
                  </form>
                )}
                {!canRequestReview && (
                  <div className="mt-4 flex justify-end">
                    <button
                      type="button"
                      onClick={handleClose}
                      className="px-2.5 py-1 text-xs font-medium rounded-md transition-colors bg-gray-100 text-gray-600 hover:bg-gray-200 dark:bg-slate-700 dark:text-slate-300"
                    >
                      Close
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <div className="mt-5 pt-4 border-t border-gray-100 dark:border-slate-700 flex flex-wrap items-center justify-end gap-3">
                {!chosenTerm && (
                  <span className="text-[11px] text-gray-500 dark:text-slate-400">Choose a repayment period to continue</span>
                )}
                <button
                  type="button"
                  onClick={handleContinueToApplication}
                  disabled={!chosenTerm}
                  title={chosenTerm ? undefined : 'Choose a repayment period first'}
                  className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-xs font-semibold bg-blue-600 text-white hover:bg-blue-700 shadow-sm transition disabled:bg-gray-300 disabled:text-gray-500 disabled:cursor-not-allowed dark:disabled:bg-slate-700 dark:disabled:text-slate-400"
                >
                  Continue to Application
                  <ChevronRight size={14} />
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  const renderDisabledNotice = () => (
    <div className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 shadow-sm overflow-hidden">
      <div className="h-0.5 bg-gradient-to-r from-slate-400 to-slate-500" />
      <div className="p-4 sm:p-6">
        <div className="flex items-center gap-2.5">
          <div className="w-9 h-9 rounded-lg bg-slate-100 dark:bg-slate-700 text-slate-500 dark:text-slate-300 flex items-center justify-center flex-shrink-0">
            <ShieldCheck size={18} />
          </div>
          <div>
            <h3 className="text-sm font-semibold text-gray-900 dark:text-slate-100">{heading}</h3>
            <p className="text-[11px] text-gray-500 dark:text-slate-400">Not available on this installation</p>
          </div>
        </div>
        <p className="mt-4 text-xs text-gray-600 dark:text-slate-300">
          AI verification is disabled on this installation — continue with manual capture.
        </p>
        {onCancel && (
          <div className="mt-5 pt-4 border-t border-gray-100 dark:border-slate-700 flex justify-end">
            <button
              type="button"
              onClick={onCancel}
              className="px-4 py-2 rounded-lg text-xs font-medium bg-gray-100 dark:bg-slate-700 text-gray-700 dark:text-slate-200 hover:bg-gray-200 dark:hover:bg-slate-600 transition"
            >
              Close
            </button>
          </div>
        )}
      </div>
    </div>
  );

  if (!aiEnabled || aiDisabled) return renderDisabledNotice();
  if (phase === 'verifying') return renderVerifyingPhase();
  if (phase === 'review') return renderReviewPhase();
  return renderDocumentsPhase();
};

export default PreQualification;
